"use client";

import { motion, type Variants } from "motion/react";
import Link from "next/link";
import type { ReactNode } from "react";

import { REVEAL_STAGGER, TextReveal } from "@/components/elements/text-reveal";

const REVEAL_ITEM: Variants = {
  hidden: { opacity: 0, y: 24, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  },
};

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="text-text-tertiary font-mono text-xs font-medium tracking-[3px] uppercase">
      {children}
    </span>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return (
    <h2 className="font-display text-3xl tracking-tight text-balance md:text-[40px] md:leading-tight">
      {children}
    </h2>
  );
}

/**
 * Keyword-rich prose block (SEO intro). `children` may include internal links —
 * anchors are styled inline. Centered, matches the hero/quote rhythm.
 */
export function SeoIntro({
  eyebrow,
  heading,
  children,
}: {
  eyebrow?: string;
  heading?: string;
  children: ReactNode;
}) {
  return (
    <section className="section-padding">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={{ hidden: {}, visible: {} }}
          className="mx-auto max-w-3xl text-center"
        >
          {eyebrow && (
            <TextReveal mode="animate" className="mb-5">
              <Eyebrow>{eyebrow}</Eyebrow>
            </TextReveal>
          )}
          {heading && (
            <TextReveal mode="animate" delay={REVEAL_STAGGER} className="mb-6">
              <Heading>{heading}</Heading>
            </TextReveal>
          )}
          <TextReveal mode="animate" delay={REVEAL_STAGGER * 2}>
            <div className="text-muted-foreground mx-auto max-w-[680px] text-base leading-7 [&_a]:text-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:opacity-70">
              {children}
            </div>
          </TextReveal>
        </motion.div>
      </div>
    </section>
  );
}

/** Numbered how-it-works steps (typically 3–4). */
export function HowItWorks({
  eyebrow = "How it works",
  heading,
  steps,
}: {
  eyebrow?: string;
  heading: string;
  steps: readonly { title: string; description: string }[];
}) {
  return (
    <section className="section-padding">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{ hidden: {}, visible: {} }}
          className="mb-14 flex flex-col items-center gap-4 text-center md:mb-20"
        >
          <TextReveal mode="animate">
            <Eyebrow>{eyebrow}</Eyebrow>
          </TextReveal>
          <TextReveal mode="animate" delay={REVEAL_STAGGER}>
            <Heading>{heading}</Heading>
          </TextReveal>
        </motion.div>

        <motion.ol
          className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.1 } },
          }}
        >
          {steps.map((step, i) => (
            <motion.li
              key={step.title}
              className="flex flex-col items-center text-center"
              variants={REVEAL_ITEM}
            >
              <span className="font-display bg-secondary text-secondary-foreground mb-6 flex h-12 w-12 items-center justify-center rounded-full text-lg">
                {i + 1}
              </span>
              <h3 className="font-display max-w-[252px] text-xl leading-[26px] tracking-tight">
                {step.title}
              </h3>
              <p className="text-muted-foreground mt-2.5 max-w-[252px] text-sm leading-[22px]">
                {step.description}
              </p>
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </section>
  );
}

function Check() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 20 20"
      className="mt-0.5 h-5 w-5 shrink-0"
      fill="none"
    >
      <circle cx="10" cy="10" r="10" className="fill-secondary" />
      <path
        d="M6 10.5l2.5 2.5L14 7.5"
        className="stroke-secondary-foreground"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Heading + check-marked reasons, in a 2-column list. */
export function ReasonList({
  eyebrow,
  heading,
  description,
  items,
}: {
  eyebrow?: string;
  heading: string;
  description?: string;
  items: readonly string[];
}) {
  return (
    <section className="section-padding">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: {} }}
          className="mx-auto max-w-3xl text-center"
        >
          {eyebrow && (
            <TextReveal mode="animate" className="mb-5">
              <Eyebrow>{eyebrow}</Eyebrow>
            </TextReveal>
          )}
          <TextReveal mode="animate" delay={REVEAL_STAGGER}>
            <Heading>{heading}</Heading>
          </TextReveal>
          {description && (
            <TextReveal mode="animate" delay={REVEAL_STAGGER * 2} className="mt-6">
              <p className="text-muted-foreground mx-auto max-w-[620px] text-base leading-7">
                {description}
              </p>
            </TextReveal>
          )}
        </motion.div>

        <motion.ul
          className="mx-auto mt-12 grid max-w-3xl grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08 } },
          }}
        >
          {items.map((item) => (
            <motion.li
              key={item}
              className="flex items-start gap-3 text-left"
              variants={REVEAL_ITEM}
            >
              <Check />
              <span className="text-foreground text-base leading-7">{item}</span>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}

/** Heading + a grid of linked cards (e.g. a use-case hub). */
export function LinkGrid({
  eyebrow,
  heading,
  items,
}: {
  eyebrow?: string;
  heading: string;
  items: readonly { label: string; href: string; description: string }[];
}) {
  return (
    <section className="section-padding">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: {} }}
          className="mb-12 flex flex-col items-center gap-4 text-center md:mb-16"
        >
          {eyebrow && (
            <TextReveal mode="animate">
              <Eyebrow>{eyebrow}</Eyebrow>
            </TextReveal>
          )}
          <TextReveal mode="animate" delay={REVEAL_STAGGER}>
            <Heading>{heading}</Heading>
          </TextReveal>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08 } },
          }}
        >
          {items.map((item) => (
            <motion.div key={item.href} variants={REVEAL_ITEM}>
              <Link
                href={item.href}
                className="border-foreground/10 bg-surface hover:border-foreground/25 group block h-full rounded-2xl border p-6 no-underline transition-colors"
              >
                <h3 className="font-display text-foreground text-xl tracking-tight">
                  {item.label}
                </h3>
                <p className="text-muted-foreground mt-2 text-sm leading-6">
                  {item.description}
                </p>
                <span className="text-foreground mt-4 inline-block text-sm">
                  Explore{" "}
                  <span className="inline-block transition-transform group-hover:translate-x-0.5">
                    →
                  </span>
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/** Heading + pill chips (e.g. "Perfect for" moments). */
export function MomentTags({
  eyebrow,
  heading,
  items,
}: {
  eyebrow?: string;
  heading: string;
  items: readonly string[];
}) {
  return (
    <section className="section-padding">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{ hidden: {}, visible: {} }}
          className="mx-auto max-w-3xl text-center"
        >
          {eyebrow && (
            <TextReveal mode="animate" className="mb-5">
              <Eyebrow>{eyebrow}</Eyebrow>
            </TextReveal>
          )}
          <TextReveal mode="animate" delay={REVEAL_STAGGER}>
            <Heading>{heading}</Heading>
          </TextReveal>
        </motion.div>

        <motion.ul
          className="mx-auto mt-10 flex max-w-2xl flex-wrap justify-center gap-3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.05 } },
          }}
        >
          {items.map((item) => (
            <motion.li
              key={item}
              className="border-foreground/12 bg-surface text-foreground rounded-full border px-4 py-2 text-sm"
              variants={REVEAL_ITEM}
            >
              {item}
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
